/**
 * Compliant cosmetic label generator (v2).
 *
 * Coverage additions over v1:
 *   1. Multilingual mandatory text (EN/DE/FR/IT/ES/PT/NL) per Reg. 1223/2009 Article 19(5)
 *   2. Dual-unit net content validation (FPLA + Directive 80/181/EEC)
 *   3. Inline allergen rendering inside the INCI list (per EU practice)
 *   4. Colorant detection and "[+/-]" suffix per Article 19(1)(g)
 *   5. Concentration-gated Annex III warnings — only fire above thresholds
 *   6. Pictograms — PAO, hourglass-BBE, open-book leaflet, Möbius loop, e-mark
 *   7. PDP geometry — derive minimum letter heights per 21 CFR § 701.13(g)
 *   8. California Prop 65 + state-level US bans (CA SB-484, WA, NY, MD)
 *   9. Nano-form "(nano)" suffix per Article 19(1)(g)
 *  10. Refill / small-package leaflet exemption (Article 19(2))
 */

import mocraReference from "../data/mocra_reference.json";
import labelStrings from "../data/labelStrings.json";
import { evaluateStateLabelRules } from "./labelStateRules";
import { euAllergens } from "./compliance";

const EU_REG_URL =
  "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A02009R1223-20240424";
const FPLA_URL = "https://www.law.cornell.edu/uscode/text/15/chapter-39";
const CFR_701_URL = "https://www.ecfr.gov/current/title-21/chapter-I/subchapter-G/part-701";
const CFR_740_URL = "https://www.ecfr.gov/current/title-21/chapter-I/subchapter-G/part-740";
const CFR_134_URL = "https://www.ecfr.gov/current/title-19/chapter-I/part-134";
const PROP_65_URL = "https://oehha.ca.gov/proposition-65/proposition-65-list";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const titleCase = (value) =>
  String(value || "").replace(/\b\w/g, (c) => c.toUpperCase());

const normalizeText = (value) =>
  String(value || "").toLowerCase().replace(/\s+/g, " ").trim();

const normalizeCas = (value) => normalizeText(value).replace(/\s+/g, "");

const splitCasList = (value) =>
  (Array.isArray(value) ? value : [value])
    .flatMap((entry) => String(entry || "").split(/\s*,\s*/))
    .map(normalizeCas)
    .filter(Boolean);

const checkField = ({ key, label, value, citation, citationUrl, optional = false }) => {
  const present = optional ? true : Boolean(value && String(value).trim());
  return { key, label, present, value: value || "", citation, citationUrl };
};

const t = (key, lang = "en") => {
  const entry = labelStrings[key];
  if (!entry) return key;
  return entry[lang] || entry.en || key;
};

// ---------------------------------------------------------------------------
// Net content — dual-unit detection (FIX #2)
// ---------------------------------------------------------------------------

// Returns { metric, customary, raw, hasBoth } and best-effort auto-conversion.
export const parseNetContent = (raw = "") => {
  const text = String(raw || "").trim();
  const hasMetric = /\b(\d+(?:[.,]\d+)?)\s*(ml|l|g|kg)\b/i.test(text);
  const hasCustomary = /\b(\d+(?:[.,]\d+)?)\s*(fl\.?\s*oz|oz|lb|fluid\s+ounce|ounce|pound)/i.test(text);
  return {
    raw: text,
    hasMetric,
    hasCustomary,
    hasBoth: hasMetric && hasCustomary,
  };
};

// ---------------------------------------------------------------------------
// Ingredient classification — colorants, nano, allergens (FIX #4, #9)
// ---------------------------------------------------------------------------

const COLORANT_PATTERN = /^\s*(ci\s?\d{2,5}|d&c\s|fd&c\s|cl\s?\d{2,5})/i;

const isColorant = (ingredient) => {
  const name = ingredient.inciName || ingredient.ingredient_name || "";
  if (COLORANT_PATTERN.test(name)) return true;
  // Common colorant CAS numbers (subset; expand as needed)
  const colorantCas = new Set([
    "1390-65-4", "1325-87-7", "2353-45-9", "2580-78-1",
  ]);
  return splitCasList(ingredient.cas_number).some((c) => colorantCas.has(c));
};

const NANO_HINT_PATTERN = /\(nano\)/i;

const isNanoForm = (ingredient) => {
  if (ingredient.nanoForm === true || ingredient.is_nano === true) return true;
  if (NANO_HINT_PATTERN.test(ingredient.ingredient_name || "")) return true;
  if (NANO_HINT_PATTERN.test(ingredient.inciName || "")) return true;
  // Caller can opt in via explicit flag; we don't auto-flag bulk TiO2.
  return false;
};

const formatInciName = (ingredient, { isNano = false } = {}) => {
  const base = titleCase(ingredient.ingredient_name || "");
  if (isNano && !NANO_HINT_PATTERN.test(base)) {
    return `${base} (nano)`;
  }
  return base;
};

// ---------------------------------------------------------------------------
// Product-class detection — drives category-specific labelling rules.
//
// Sunscreens: in the US, FDA regulates them as OTC drugs requiring a Drug
// Facts panel per 21 CFR § 201.66; in the EU, Reg. 1223/2009 + Commission
// Recommendation 2006/647/EC requires sun-protection-specific warnings.
// Hair dyes: trigger Annex III §11 sensitisation warning.
// AHA / retinoid skincare: triggers 21 CFR § 740.18 sun-sensitivity warning
// in the US.
// ---------------------------------------------------------------------------

const SUNSCREEN_TERMS = /(sunscreen|sun\s*care|spf|sun\s*protection|after\s*sun|broad[-\s]?spectrum)/i;
const HAIR_DYE_TERMS = /(hair\s*dye|hair\s*colour|hair\s*color|oxidative\s*hair|bleach\s*hair)/i;
const AHA_TERMS = /(aha|alpha[\s-]?hydroxy|glycolic|lactic acid|salicylic|retinol|retinyl|retinoic)/i;

const productClassFromProduct = (product = {}) => {
  const haystack = [
    product.category,
    product.function,
    product.name,
    ...(product.claims || []),
  ]
    .filter(Boolean)
    .join(" ");
  if (SUNSCREEN_TERMS.test(haystack)) return "sunscreen";
  if (HAIR_DYE_TERMS.test(haystack)) return "hair-dye";
  if (AHA_TERMS.test(haystack)) return "aha-skincare";
  return "cosmetic";
};

const extractSpfNumber = (product = {}) => {
  const haystack = [product.name, product.function, ...(product.claims || [])]
    .filter(Boolean)
    .join(" ");
  const m = haystack.match(/SPF\s*(\d{1,3})/i);
  return m ? Number(m[1]) : null;
};

// SPF labelling category per Commission Recommendation 2006/647/EC.
const spfCategory = (spf) => {
  if (!Number.isFinite(spf)) return null;
  if (spf >= 50) return spf >= 60 ? "Very High (50+)" : "Very High";
  if (spf >= 30) return "High";
  if (spf >= 15) return "Medium";
  if (spf >= 6) return "Low";
  return null;
};

// Identify UV-filter active ingredients (Annex VI matches) for the Drug
// Facts active-ingredient panel.
const splitUvActives = (ifraResults = [], regulatoryRules = []) => {
  const annexVi = regulatoryRules.filter(
    (r) => r.jurisdiction === "EU" && r.annex === "VI"
  );
  const annexViCas = new Set(
    annexVi.flatMap((r) => (r.casNumbers || []).map(normalizeCas))
  );
  const annexViNames = new Set(
    annexVi
      .flatMap((r) => [r.inciName, r.substanceName])
      .map(normalizeText)
      .filter(Boolean)
  );
  const actives = [];
  const inactives = [];
  ifraResults.forEach((row) => {
    const cas = normalizeCas(row.cas_number);
    const name = normalizeText(row.ingredient_name);
    const isUvFilter =
      (cas && annexViCas.has(cas)) ||
      (name && [...annexViNames].some((n) => name.includes(n) || n.includes(name)));
    if (isUvFilter) actives.push(row);
    else inactives.push(row);
  });
  return { actives, inactives };
};

// ---------------------------------------------------------------------------
// Ingredient list builder — applies all sorting + colorants + nano (FIX #3, #4, #9)
// ---------------------------------------------------------------------------

// Decide whether a row is a fragrance ingredient that may legally hide
// under the generic "Parfum" / "Fragrance" INCI entry. Conservative —
// only collapses ingredients that are unambiguously fragrance-purpose
// (in the IFRA Standards library or explicitly declared by the user).
// Multi-function CosIng entries (Ethanol, Glycerin, Propylene Glycol)
// are NOT collapsed even if their function list mentions fragrance.
const isFragranceIngredient = (row) => {
  if (row.document_name) return true; // matched an IFRA Standard
  const name = String(row.ingredient_name || "").toLowerCase().trim();
  if (/^(parfum|fragrance|perfume oil|aroma|fragrance compound)$/.test(name)) return true;
  const fn = String(row.cosing?.function || "").toLowerCase().trim();
  // Only auto-collapse when CosIng's sole function is fragrance (a few
  // small synonyms ride along — "fragrance" plus "masking" is still a
  // pure-fragrance use).
  if (fn === "fragrance" || fn === "fragrance, masking" || fn === "masking, fragrance") return true;
  return false;
};

// EU allergen labelling thresholds (Reg. 1223/2009 Annex III). Used by the
// label builder to decide which fragrance allergens MUST be surfaced as
// their own INCI line even when the rest of the fragrance compound is
// hidden under "Parfum".
const FRAGRANCE_LABEL_TERM = { EU: "Parfum", US: "Fragrance" };

const buildIngredientList = (
  ifraResults,
  allergenSet,
  lang,
  { jurisdiction = "EU", allergenThreshold = 0.001 } = {}
) => {
  // Include valid ingredients AND colorants (CI ####) — colorants don't pass
  // the IFRA fragrance search but must still appear on the label.
  const enriched = ifraResults
    .filter((i) => {
      if (i.validIngredient) return true;
      // Colorants are recognized by name pattern regardless of IFRA validity.
      return isColorant(i);
    })
    .map((i) => {
      const colorant = isColorant(i);
      const nano = isNanoForm(i);
      const inciName = formatInciName(i, { isNano: nano });
      const inciKey = normalizeText(inciName.replace(/\s*\(nano\)$/i, ""));
      const isAllergen = allergenSet.has(inciKey);
      return {
        ...i,
        inciName,
        isColorant: colorant,
        isNano: nano,
        isAllergen,
        isFragrance: isFragranceIngredient(i),
      };
    });

  const colorants = enriched.filter((i) => i.isColorant);
  const main = enriched.filter((i) => !i.isColorant);

  // SOTA INCI labelling (EU Reg. 1223/2009 Art. 19(1)(g) + Annex III; US
  // 21 CFR § 701.3): aroma-purpose ingredients can be aggregated under the
  // generic "Parfum" / "Fragrance" entry, BUT
  //   • EU Annex III declared allergens above their threshold MUST be
  //     listed individually,
  //   • nano-form materials MUST be listed individually with the [nano]
  //     suffix (Annex III §1226 + Reg. (EU) 2022/1531),
  //   • and any non-fragrance excipient (water, alcohol, glycerin, etc.)
  //     is always listed individually.
  const allergenAboveThreshold = (i) =>
    i.isAllergen &&
    Number.isFinite(Number(i.formula_percentage)) &&
    Number(i.formula_percentage) >= allergenThreshold;

  const mustBeExplicit = (i) =>
    !i.isFragrance || allergenAboveThreshold(i) || i.isNano;

  const explicit = main.filter(mustBeExplicit);
  const aggregated = main.filter((i) => !mustBeExplicit(i));

  // Build the render list. If anything was aggregated, emit a single
  // synthetic Parfum/Fragrance line at the position its weight earns.
  const renderList = [...explicit];
  let parfumEntry = null;
  if (aggregated.length > 0) {
    const aggregateTotal = aggregated.reduce(
      (sum, i) => sum + (Number(i.formula_percentage) || 0),
      0
    );
    parfumEntry = {
      inciName: FRAGRANCE_LABEL_TERM[jurisdiction] || FRAGRANCE_LABEL_TERM.EU,
      formula_percentage: Number(aggregateTotal.toFixed(6)),
      cas_number: "",
      isFragranceAggregate: true,
      contributorCount: aggregated.length,
      // Provenance for downstream UIs / audit (e.g. matrix tooltips can
      // explain what's hidden under Parfum).
      contributors: aggregated.map((i) => ({
        inciName: i.inciName,
        casNumber: i.cas_number,
        percentage: i.formula_percentage,
      })),
    };
    renderList.push(parfumEntry);
  }

  const above = renderList.filter((i) => Number(i.formula_percentage) >= 1);
  const below = renderList.filter((i) => Number(i.formula_percentage) < 1);
  above.sort((a, b) => Number(b.formula_percentage) - Number(a.formula_percentage));
  below.sort((a, b) => String(a.inciName).localeCompare(String(b.inciName)));

  const ordered = [...above, ...below];
  const tail = colorants.length
    ? [{ separator: true, label: t("may_contain", lang) || "[+/-]" }, ...colorants]
    : [];

  const fullList = [...ordered, ...tail];

  const inciLine = fullList
    .map((entry) => (entry.separator ? `[+/- ${entry.label}: ` : entry.inciName))
    .join(", ")
    .replace(/, \[\+\/-/, " [+/-")
    + (colorants.length ? "]" : "");

  return { fullList, ordered, colorants, inciLine, parfumEntry };
};

// ---------------------------------------------------------------------------
// Annex III warnings — concentration-gated (FIX #5)
// ---------------------------------------------------------------------------

const annexWarningsForIngredient = (ingredient, regulatoryRules = []) => {
  const annexRules = regulatoryRules.filter(
    (rule) => rule.jurisdiction === "EU" && rule.annex === "III"
  );
  const targetCas = normalizeCas(ingredient.cas_number);
  const targetName = normalizeText(ingredient.ingredient_name);
  const concentration = Number(ingredient.formula_percentage);
  const concentrationVerified = ingredient.concentration_verified === true;

  return annexRules
    .filter((rule) => {
      if (targetCas && targetCas !== "n/a" && splitCasList(rule.casNumbers).includes(targetCas)) return true;
      if (!targetName) return false;
      const ruleNames = [rule.substanceName, rule.inciName, ...(rule.synonyms || [])]
        .map(normalizeText)
        .filter(Boolean);
      return ruleNames.some((n) => n === targetName || n.startsWith(targetName) || targetName.startsWith(n));
    })
    .filter((rule) => {
      // Concentration gating:
      //   - `maxPercentage` is the *absolute legal limit* (substance is illegal
      //     above it), so it should NOT gate warnings.
      //   - `warningThresholdPercentage` is the concentration above which an
      //     additional label warning is required — gate on that only.
      // When the threshold isn't set, fire warnings whenever the substance is
      // present (below the legal max). When the threshold IS set but the
      // formula concentration is unverified, include conservatively.
      const warn = Number(rule.warningThresholdPercentage);
      if (!Number.isFinite(warn) || warn <= 0) return true; // no threshold gate
      if (!concentrationVerified || !Number.isFinite(concentration)) return true;
      return concentration >= warn;
    })
    .flatMap((rule) =>
      (rule.requiredWarnings || []).map((text) => ({
        substance: rule.substanceName || rule.inciName,
        referenceNumber: rule.referenceNumber,
        text: String(text).replace(/\s+/g, " ").trim(),
        sourceUrl: rule.sourceUrl || EU_REG_URL,
      }))
    );
};

// ---------------------------------------------------------------------------
// Pictograms metadata (FIX #6)
// ---------------------------------------------------------------------------

// EU pictograms: PAO/BBE/leaflet are mandatory under Reg. 1223/2009;
// Möbius and e-mark are voluntary; Triman is mandatory only for products
// placed on the French market (Article L. 541-9-3 du Code de l'environnement).
//
// Each pictogram carries `triggeredBy` so the UI can answer "why is this
// here?" without the user having to cross-reference the rules themselves.
// Detect CLP hazard classifications that drive mandatory GHS pictograms.
// Inputs:
//   ifraResults — parsed ingredient rows with CAS + concentration
//   productClass — from `productClassFromProduct`
// Returns the set of CLP hazard codes the product triggers, used by both
// the EU pictogram builder and the EU label warnings.
const detectClpHazards = (ifraResults = []) => {
  const totals = { ethanol: 0, isopropyl: 0 };
  const ALCOHOL_CAS = new Set(["64-17-5", "67-63-0"]);
  ifraResults.forEach((row) => {
    const cas = String(row.cas_number || "").trim();
    const pct = Number(row.formula_percentage);
    if (!Number.isFinite(pct)) return;
    if (ALCOHOL_CAS.has(cas)) {
      if (cas === "64-17-5") totals.ethanol += pct;
      if (cas === "67-63-0") totals.isopropyl += pct;
      return;
    }
    // Name-based fallback for cases where CAS is missing or "N/A".
    const name = String(row.ingredient_name || "").toLowerCase().trim();
    if (/^(ethanol|alcohol(\s*denat\.?)?|sd\s*alcohol|denatured\s*alcohol)\b/i.test(name)) {
      totals.ethanol += pct;
    } else if (/^(isopropyl\s*alcohol|isopropanol)\b/i.test(name)) {
      totals.isopropyl += pct;
    }
  });
  const totalAlcohol = totals.ethanol + totals.isopropyl;
  const hazards = [];
  // CLP Annex I §2.6: flammable-liquid categories by flash point.
  // Ethanol-based fragrances ≥70% wt → Flam. Liq. 2 (H225, "Highly
  // flammable liquid and vapour", DANGER). 25–70% → Flam. Liq. 3 (H226,
  // "Flammable liquid and vapour", WARNING). <25% (e.g. lotions, gels,
  // shampoos) → no flame pictogram.
  if (totalAlcohol >= 70) {
    hazards.push({
      code: "FLAM_LIQ_2",
      pictogram: "ghs02_flame",
      signalWord: "Danger",
      hazardStatement: "H225 — Highly flammable liquid and vapour.",
      precautions: [
        "P210 — Keep away from heat, hot surfaces, sparks, open flames and other ignition sources. No smoking.",
        "P233 — Keep container tightly closed.",
        "P403+P235 — Store in a well-ventilated place. Keep cool.",
      ],
      reason: `Total alcohol = ${totalAlcohol.toFixed(1)}% (Flam. Liq. 2 threshold ≥ 70%)`,
    });
  } else if (totalAlcohol >= 25) {
    hazards.push({
      code: "FLAM_LIQ_3",
      pictogram: "ghs02_flame",
      signalWord: "Warning",
      hazardStatement: "H226 — Flammable liquid and vapour.",
      precautions: [
        "P210 — Keep away from heat, hot surfaces, sparks, open flames and other ignition sources. No smoking.",
        "P403+P235 — Store in a well-ventilated place. Keep cool.",
      ],
      reason: `Total alcohol = ${totalAlcohol.toFixed(1)}% (Flam. Liq. 3 threshold ≥ 25%)`,
    });
  }
  return hazards;
};

const buildEuPictograms = ({ paoMonths, bbeRequired, smallPackage, markets = [], clpHazards = [] }) => {
  const pictograms = [];
  if (paoMonths) {
    pictograms.push({
      kind: "pao", scope: "EU", required: true,
      label: `${paoMonths} after opening`,
      citation: "Reg. 1223/2009 Annex VII §2",
      triggeredBy: `Period after opening = ${paoMonths}`,
      value: paoMonths,
    });
  }
  if (bbeRequired) {
    pictograms.push({
      kind: "hourglass", scope: "EU", required: true,
      label: "Best before date",
      citation: "Reg. 1223/2009 Article 19(1)(d)",
      triggeredBy: "Shelf life ≤ 30 months — date of minimum durability required",
    });
  }
  if (smallPackage) {
    pictograms.push({
      kind: "open_book", scope: "EU", required: true,
      label: "See enclosed leaflet",
      citation: "Reg. 1223/2009 Annex VII §1",
      triggeredBy: "Small-package toggle is ON — leaflet referral required",
    });
  }
  // Triman — mandatory in France only.
  if (markets.some((m) => /^fr$|france/i.test(String(m)))) {
    pictograms.push({
      kind: "triman", scope: "FR", required: true,
      label: "Triman (FR recyclability)",
      citation: "Article L. 541-9-3 du Code de l'environnement",
      triggeredBy: "France is in product markets",
    });
  }
  // CLP Reg. (EC) 1272/2008 hazard pictograms — mandatory when the
  // cosmetic product also qualifies as a hazardous mixture (e.g. ≥25%
  // alcohol → Flam. Liq. 3 → GHS02 Flame). These are layered onto the
  // Reg. 1223/2009 cosmetic pictograms; both regimes apply in parallel.
  clpHazards.forEach((hz) => {
    pictograms.push({
      kind: hz.pictogram,
      scope: "EU/CLP",
      required: true,
      label: hz.hazardStatement,
      citation: `CLP ${hz.code} (${hz.signalWord})`,
      triggeredBy: hz.reason,
    });
  });
  // Möbius loop (94/62/EC) and e-mark (76/211/EEC) — voluntary EU markings.
  pictograms.push({
    kind: "mobius", scope: "EU", optional: true,
    label: "Recyclable packaging marking",
    citation: "Directive 94/62/EC",
    triggeredBy: "Voluntary — recommended on all EU cosmetic packaging",
  });
  pictograms.push({
    kind: "e_mark", scope: "EU", optional: true,
    label: "e-mark (estimated nominal quantity)",
    citation: "Directive 76/211/EEC",
    triggeredBy: "Voluntary — applies when packaged under EEC nominal-quantity rules",
  });
  return pictograms;
};

// US pictograms: 21 CFR § 701 / FPLA / MoCRA prescribe TEXT statements only —
// no mandatory pictograms exist for cosmetics. The Möbius loop (ISO 7000-1135)
// is universally voluntary and commonly applied. PAO, hourglass, e-mark and
// Triman are EU-specific and intentionally excluded here.
const buildUsPictograms = () => [
  {
    kind: "mobius", scope: "US", optional: true,
    label: "Recyclable packaging marking (voluntary)",
    citation: "ISO 7000-1135",
    triggeredBy: "Voluntary — US has no mandatory cosmetic label pictograms",
  },
];

// ---------------------------------------------------------------------------
// PDP font sizing — 21 CFR § 701.13(g) (FIX #7)
// ---------------------------------------------------------------------------

// Returns minimum letter height (in inches) for the net content statement.
const computeNetContentMinLetterHeight = (pdpAreaSqIn) => {
  const a = Number(pdpAreaSqIn);
  if (!Number.isFinite(a) || a <= 0) return null;
  if (a <= 5) return 1 / 16;
  if (a <= 25) return 1 / 8;
  if (a <= 100) return 3 / 16;
  if (a <= 400) return 1 / 4;
  return 1 / 2;
};

const computePdpGeometry = (product) => {
  const area = Number(product.packaging?.pdpAreaSqIn);
  const minLetterHeightIn = computeNetContentMinLetterHeight(area);
  return {
    pdpAreaSqIn: Number.isFinite(area) ? area : null,
    minLetterHeightIn,
    minLetterHeightMm: minLetterHeightIn ? Math.round(minLetterHeightIn * 25.4 * 10) / 10 : null,
    note:
      "21 CFR § 701.13(g): net quantity must occupy bottom 30% of PDP. Minimum letter height scales with PDP area.",
  };
};

const isLeaveOn = (product) => {
  const hay = normalizeText(
    [product.function, product.category, ...(product.claims || [])].filter(Boolean).join(" ")
  );
  return !/rinse|wash|cleanser|cleanse|shampoo|conditioner|shower gel|soap|exfoliat/.test(hay);
};

// EU allergen declaration thresholds.
const EU_ALLERGEN_THRESHOLDS = { leaveOn: 0.001, rinseOff: 0.01 };

// PAO/BBE formatting.
const formatPao = (pao) => {
  if (!pao) return "";
  const match = String(pao).match(/(\d+)\s*M?/i);
  if (match) return `${match[1]}M`;
  return String(pao);
};
const formatBbe = (date) => {
  if (!date) return "";
  if (date instanceof Date) return date.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
  const parsed = new Date(date);
  if (!Number.isNaN(parsed.getTime())) return parsed.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
  return String(date);
};

// ---------------------------------------------------------------------------
// EU label generator
// ---------------------------------------------------------------------------

export const generateEuLabel = ({
  product = {},
  compliance = {},
  regulatoryRules = [],
  language = "en",
} = {}) => {
  const lang = labelStrings.metadata.languages.includes(language) ? language : "en";
  const activeFormula = compliance.activeFormula || {};
  const ifraResults = activeFormula.ifraResults || [];
  const allergenDetailsRaw = compliance.jurisdictions?.EU?.facts?.allergens || [];
  const allergenSet = new Set(allergenDetailsRaw.map(normalizeText));

  // Allergen declaration metadata (still tracked for the checklist UI).
  // Computed before buildIngredientList so we can pass the threshold into
  // it — Parfum aggregation needs to know which allergens to surface.
  const threshold = isLeaveOn(product)
    ? EU_ALLERGEN_THRESHOLDS.leaveOn
    : EU_ALLERGEN_THRESHOLDS.rinseOff;

  // Ingredient list with inline allergens, colorants, nano (FIX #3, #4, #9)
  // and SOTA Parfum aggregation under EU Article 19(1)(g) + Annex III.
  const ingredientBuild = buildIngredientList(ifraResults, allergenSet, lang, {
    jurisdiction: "EU",
    allergenThreshold: threshold,
  });
  const ingredientLine = ingredientBuild.inciLine;

  // Annex III warnings — concentration-gated (FIX #5)
  const annexWarnings = ifraResults.flatMap((ingredient) =>
    annexWarningsForIngredient(ingredient, regulatoryRules)
  );

  // Category-specific EU labelling: Commission Recommendation 2006/647/EC
  // for sunscreens (sun protection category + usage messages); Annex III §11
  // for hair dyes; sun-sensitivity for AHA-skincare.
  const productClass = productClassFromProduct(product);
  const spfNumber = extractSpfNumber(product);
  const categoryWarnings = [];
  if (productClass === "sunscreen") {
    categoryWarnings.push({
      code: "EU_SUNSCREEN_USAGE",
      text: "Apply liberally before sun exposure to obtain the indicated sun protection factor. Reapply frequently to maintain protection, especially after sweating, swimming, or towelling. Avoid the midday sun. Do not stay too long in the sun even while using a sunscreen product. Keep babies and young children out of direct sunlight.",
      citation: "Commission Recommendation 2006/647/EC",
      citationUrl: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:32006H0647",
    });
    if (spfNumber) {
      categoryWarnings.push({
        code: "EU_SUNSCREEN_SPF_CATEGORY",
        text: `SPF ${spfNumber} — sun-protection category: ${spfCategory(spfNumber) || "n/a"} (Commission Recommendation 2006/647/EC).`,
        citation: "Commission Recommendation 2006/647/EC",
        citationUrl: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:32006H0647",
      });
    }
  }
  if (productClass === "hair-dye") {
    categoryWarnings.push({
      code: "EU_HAIR_DYE_SENSITISATION_WARNING",
      text: "Hair colorants can cause severe allergic reactions. Read and follow instructions. This product is not intended for use on persons under the age of 16. Temporary 'black henna' tattoos may increase your risk of allergy. Do not colour your hair if you have a rash on your face or sensitive, irritated and damaged scalp; if you have ever experienced any reaction after colouring your hair; or if you have experienced a reaction to a temporary 'black henna' tattoo in the past.",
      citation: "Reg. (EC) 1223/2009 Annex III §11",
      citationUrl: EU_REG_URL,
    });
  }
  if (productClass === "aha-skincare") {
    categoryWarnings.push({
      code: "EU_AHA_SUN_SENSITIVITY",
      text: "This product contains an alpha hydroxy acid (AHA). It may increase skin sensitivity to the sun. Use a sunscreen and limit sun exposure while using this product and for one week after.",
      citation: "Reg. (EC) 1223/2009 — claims & safety guidance",
      citationUrl: EU_REG_URL,
    });
  }

  // PAO + BBE (FIX #6 partial via pictograms)
  const paoMonths = formatPao(product.packaging?.pao);
  const minimumDurability = formatBbe(product.packaging?.minimumDurability);
  const bbeRequired = !paoMonths;
  const smallPackage = Boolean(product.packaging?.smallPackage);

  // Net content dual-unit validation (FIX #2)
  const netContent = parseNetContent(product.netContent);

  // CLP Reg. (EC) 1272/2008 hazard classification (drives GHS pictograms +
  // required hazard / precautionary statements alongside the cosmetic label).
  const clpHazards = detectClpHazards(ifraResults);
  clpHazards.forEach((hz) => {
    categoryWarnings.push({
      code: `CLP_${hz.code}`,
      text: `${hz.signalWord}. ${hz.hazardStatement} ${hz.precautions.join(" ")}`,
      citation: `Reg. (EC) 1272/2008 (CLP) — ${hz.code}`,
      citationUrl: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:02008R1272-20240115",
    });
  });

  // Pictograms (FIX #6) — EU-specific set + CLP GHS where applicable.
  const pictograms = buildEuPictograms({
    paoMonths,
    bbeRequired,
    smallPackage,
    markets: product.markets || [],
    clpHazards,
  });

  const requiredFields = [
    checkField({ key: "responsiblePerson", label: "Name of responsible person", value: product.responsiblePerson, citation: "Reg. (EC) 1223/2009, Article 19(1)(a)", citationUrl: EU_REG_URL }),
    checkField({ key: "responsibleAddress", label: "Address of responsible person", value: product.responsibleAddress, citation: "Reg. (EC) 1223/2009, Article 19(1)(a)", citationUrl: EU_REG_URL }),
    checkField({ key: "countryOfOrigin", label: "Country of origin (for imported products)", value: product.countryOfOrigin, citation: "Reg. (EC) 1223/2009, Article 19(1)(b)", citationUrl: EU_REG_URL, optional: !product.markets?.includes("EU") || normalizeText(product.countryOfOrigin) === "european union" }),
    checkField({ key: "netContent", label: "Nominal content (net quantity)", value: product.netContent, citation: "Reg. (EC) 1223/2009, Article 19(1)(c)", citationUrl: EU_REG_URL }),
    checkField({ key: "durability", label: bbeRequired ? "Date of minimum durability (BBE)" : "Period after opening (PAO)", value: bbeRequired ? minimumDurability : paoMonths, citation: "Reg. (EC) 1223/2009, Article 19(1)(d)", citationUrl: EU_REG_URL }),
    checkField({ key: "batch", label: "Batch number / lot identifier", value: product.packaging?.batchFormat, citation: "Reg. (EC) 1223/2009, Article 19(1)(f)", citationUrl: EU_REG_URL }),
    checkField({ key: "function", label: "Function of the product", value: product.function, citation: "Reg. (EC) 1223/2009, Article 19(1)(f)", citationUrl: EU_REG_URL }),
    checkField({ key: "ingredients", label: "List of ingredients (INCI, descending weight)", value: ingredientLine, citation: "Reg. (EC) 1223/2009, Article 19(1)(g)", citationUrl: EU_REG_URL }),
    checkField({ key: "productName", label: "Product identity / brand", value: product.name || activeFormula.name, citation: "Reg. (EC) 1223/2009, Article 19(1)(b)", citationUrl: EU_REG_URL }),
  ];

  const missingFields = requiredFields.filter((f) => !f.present);
  const isReady = missingFields.length === 0;

  // Validation flags additional to required-field presence.
  const validationFlags = [];
  if (smallPackage) {
    validationFlags.push({
      kind: "info",
      label: t("see_leaflet", lang),
      citation: "Reg. (EC) 1223/2009 Article 19(2)",
      citationUrl: EU_REG_URL,
    });
  }

  // Panels — allergens are now inline in the ingredient list (FIX #3).
  const panels = {
    front: {
      heading: "Front panel",
      lines: [
        { key: "brand", text: product.brand || "", weight: 600, fontSize: "1rem" },
        { key: "name", text: product.name || activeFormula.name || "", weight: 700, fontSize: "1.5rem" },
        { key: "function", text: product.function || "", color: "#6B7280", fontSize: "0.875rem" },
        { key: "netContent", text: product.netContent || "—", fontSize: "0.875rem", required: true },
      ].filter((line) => line.text || line.required),
    },
    information: {
      heading: "Information panel",
      lines: [
        { key: "ingredients-label", text: t("ingredients_label", lang), weight: 600, fontSize: "0.75rem", uppercase: true },
        { key: "ingredients", text: ingredientLine || "—", fontSize: "0.7rem", lineHeight: 1.5 },
        ...(categoryWarnings.length > 0
          ? [
              { key: "category-warnings-label", text: t("warnings", lang), weight: 600, fontSize: "0.75rem", uppercase: true, mt: 1 },
              ...categoryWarnings.map((w, i) => ({
                key: `category-warning-${i}`,
                text: w.text,
                fontSize: "0.7rem",
                color: "#92400E",
                lineHeight: 1.5,
              })),
            ]
          : []),
        ...(annexWarnings.length > 0
          ? [
              { key: "warnings-label", text: t("warnings", lang), weight: 600, fontSize: "0.75rem", uppercase: true, mt: categoryWarnings.length > 0 ? 1 : 1 },
              ...annexWarnings.slice(0, 6).map((w, i) => ({
                key: `warning-${i}`,
                text: w.text,
                fontSize: "0.7rem",
                color: "#374151",
                lineHeight: 1.5,
              })),
            ]
          : []),
      ],
    },
    legal: {
      heading: "Legal / responsible person panel",
      lines: [
        { key: "rp-label", text: t("responsible_person", lang), weight: 600, fontSize: "0.7rem", uppercase: true },
        { key: "rp-name", text: product.responsiblePerson || "—", fontSize: "0.75rem", required: true },
        { key: "rp-address", text: product.responsibleAddress || "—", fontSize: "0.7rem", color: "#374151", required: true },
        ...(product.countryOfOrigin
          ? [{ key: "country", text: `${t("country_of_origin", lang)}: ${product.countryOfOrigin}`, fontSize: "0.7rem", color: "#374151" }]
          : []),
        bbeRequired
          ? { key: "bbe", text: `${t("best_before", lang)}: ${minimumDurability || "—"}`, fontSize: "0.7rem", required: true, symbol: "hourglass" }
          : { key: "pao", text: `${paoMonths || "—"}`, fontSize: "0.7rem", required: true, symbol: "pao" },
        { key: "batch", text: `${t("batch", lang)}: ${product.packaging?.batchFormat || "—"}`, fontSize: "0.7rem", required: true },
        ...(smallPackage
          ? [{ key: "leaflet", text: t("see_leaflet", lang), fontSize: "0.7rem", color: "#374151", symbol: "open_book" }]
          : []),
      ],
    },
  };

  return {
    jurisdiction: "EU",
    language: lang,
    isReady,
    requiredFields,
    missingFields,
    panels,
    annexWarnings,
    categoryWarnings,
    clpHazards,
    productClass,
    spfNumber,
    spfCategory: spfCategory(spfNumber),
    validationFlags,
    pictograms,
    smallPackage,
    netContent: { ...netContent, required: "Metric (mL/g/L) — Directive 80/181/EEC" },
    allergenDeclaration: {
      required: allergenDetailsRaw.length > 0,
      threshold,
      thresholdLabel: `${threshold * 100}%`,
      productType: isLeaveOn(product) ? "leave-on" : "rinse-off",
      ingredients: allergenDetailsRaw,
      inlineInIngredientList: true,
      note: "Allergens are listed inline within the INCI ingredient list at their normal weight position (per EU practice).",
    },
    ingredientList: ingredientBuild.fullList.filter((e) => !e.separator).map((i) => ({
      inciName: i.inciName,
      casNumber: i.cas_number,
      percentage: i.formula_percentage,
      verified: i.concentration_verified === true,
      isAllergen: i.isAllergen,
      isColorant: i.isColorant,
      isNano: i.isNano,
      isFragranceAggregate: i.isFragranceAggregate === true,
      contributorCount: i.contributorCount,
      contributors: i.contributors,
    })),
    paoMonths,
    minimumDurability,
    bbeRequired,
    citations: [
      { article: "Article 19(1)", regulation: "Reg. (EC) 1223/2009", url: EU_REG_URL },
      { article: "Article 19(2) (small-package leaflet)", regulation: "Reg. (EC) 1223/2009", url: EU_REG_URL },
      { article: "Article 19(5) (language requirement)", regulation: "Reg. (EC) 1223/2009", url: EU_REG_URL },
      { article: "Annex III", regulation: "Reg. (EC) 1223/2009", url: EU_REG_URL },
      { article: "Annex VII §1 (open-book pictogram)", regulation: "Reg. (EC) 1223/2009", url: EU_REG_URL },
      { article: "Annex VII §2 (PAO pictogram)", regulation: "Reg. (EC) 1223/2009", url: EU_REG_URL },
      { article: "Directive 80/181/EEC (metric units)", regulation: "EU metric units", url: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A01980L0181-20090527" },
    ],
  };
};

// ---------------------------------------------------------------------------
// US label generator (with state-level rules — FIX #8)
// ---------------------------------------------------------------------------

export const generateUsLabel = ({ product = {}, compliance = {}, regulatoryRules = [] } = {}) => {
  const activeFormula = compliance.activeFormula || {};
  const ifraResults = activeFormula.ifraResults || [];
  const mocraFacts = compliance.jurisdictions?.US?.facts?.mocra || {};

  // US labels can legally hide every fragrance ingredient under the
  // generic "Fragrance" entry (21 CFR § 701.3). MoCRA Section 605 will
  // require allergen disclosure once FDA finalises rulemaking; until
  // then we mirror EU's Annex III allergen list as a forward-looking
  // compliance position so allergen info is preserved when present.
  const usAllergenSet = new Set(
    euAllergens.map((a) => normalizeText(a.inciName || a.name))
  );
  const ingredientBuild = buildIngredientList(ifraResults, usAllergenSet, "en", {
    jurisdiction: "US",
    allergenThreshold: 0.001,
  });
  const ingredientLine = ingredientBuild.inciLine;

  const isProfessionalUse = /(professional|salon|stylist only|pro use)/i.test(
    `${product.function || ""} ${product.category || ""} ${(product.claims || []).join(" ")}`
  );

  const adverseEventContact =
    product.adverseEventContact || product.responsibleEmail || product.responsiblePhone || "";
  const contactPresent = Boolean(
    adverseEventContact || product.responsibleAddress || product.responsiblePhone
  );

  // Net content dual-unit validation (FIX #2)
  const netContent = parseNetContent(product.netContent);

  // PDP geometry (FIX #7)
  const pdpGeometry = computePdpGeometry(product);

  // State-level findings (FIX #8)
  const stateFindings = evaluateStateLabelRules({
    ingredients: ifraResults,
    markets: product.markets || ["US"],
  });

  const requiredFields = [
    checkField({ key: "identityStatement", label: "Statement of identity (common/usual name)", value: product.name || activeFormula.name, citation: "21 CFR § 701.11", citationUrl: CFR_701_URL }),
    checkField({ key: "function", label: "Function descriptor (when name does not reveal nature)", value: product.function, citation: "21 CFR § 701.11(a)", citationUrl: CFR_701_URL }),
    checkField({ key: "netContent", label: "Net quantity of contents", value: product.netContent, citation: "21 CFR § 701.13 / FPLA § 4(a)(2)", citationUrl: CFR_701_URL }),
    checkField({
      key: "netContentDualUnit",
      label: "Net quantity dual unit (metric + US customary)",
      value: netContent.hasBoth ? product.netContent : "",
      citation: "FPLA § 4(a)(2) / 16 CFR § 500",
      citationUrl: FPLA_URL,
    }),
    checkField({ key: "responsibleFirm", label: "Name and place of business", value: product.responsiblePerson && product.responsibleAddress ? `${product.responsiblePerson}, ${product.responsibleAddress}` : "", citation: "21 CFR § 701.12 / FPLA § 4(a)(1)", citationUrl: CFR_701_URL }),
    checkField({ key: "ingredients", label: "Ingredient declaration (INCI on outer container)", value: ingredientLine, citation: "21 CFR § 701.3", citationUrl: CFR_701_URL }),
    checkField({ key: "adverseEventContact", label: "Domestic contact for adverse event reporting", value: contactPresent ? adverseEventContact || product.responsibleAddress || product.responsiblePhone : "", citation: "MoCRA § 605 / 21 USC § 364c(b)", citationUrl: mocraReference.metadata.sourceUrl }),
    checkField({ key: "countryOfOrigin", label: "Country of origin (imported products)", value: product.countryOfOrigin, citation: "19 CFR § 134", citationUrl: CFR_134_URL, optional: !product.imported && normalizeText(product.countryOfOrigin) === "united states" }),
    checkField({ key: "professionalUseStatement", label: "Professional-use statement", value: isProfessionalUse ? "For professional use only" : "", citation: "MoCRA § 364e(b)", citationUrl: mocraReference.metadata.sourceUrl, optional: !isProfessionalUse }),
  ];

  const warnings = [];
  if (mocraFacts.hasSafetySubstantiation === false) {
    warnings.push({ code: "FDA_UNSUBSTANTIATED_WARNING", text: "Warning — The safety of this product has not been determined.", citation: "21 CFR § 740.10", citationUrl: CFR_740_URL });
  }
  if (mocraFacts.containsTalc) {
    warnings.push({ code: "TALC_TESTING_DISCLOSURE", text: "Contains talc. Asbestos testing on file per 21 CFR § 700.30 (proposed).", citation: "21 CFR § 700.30 (proposed)", citationUrl: "https://www.fda.gov/cosmetics/cosmetic-ingredients/talc" });
  }
  // Category-specific warnings layered on top of MoCRA disclosures.
  const productClass = productClassFromProduct(product);
  if (productClass === "sunscreen") {
    warnings.push({
      code: "FDA_SUNSCREEN_USAGE_WARNING",
      text: "For external use only. Do not use on damaged or broken skin. When using this product, keep out of eyes — rinse with water to remove. Stop use and ask a doctor if rash occurs. Keep out of reach of children; if swallowed, get medical help or contact a Poison Control Center right away.",
      citation: "21 CFR § 201.66 (Drug Facts) / FDA OTC sunscreen final monograph",
      citationUrl: "https://www.ecfr.gov/current/title-21/chapter-I/subchapter-D/part-352",
    });
  }
  if (productClass === "aha-skincare") {
    warnings.push({
      code: "FDA_SUN_SENSITIVITY_AHA_WARNING",
      text: "Sunburn alert: This product contains an alpha hydroxy acid (AHA) that may increase your skin's sensitivity to the sun and particularly the possibility of sunburn. Use a sunscreen, wear protective clothing, and limit sun exposure while using this product and for a week afterwards.",
      citation: "21 CFR § 740.18 — sun-sensitivity alert for AHA-containing cosmetics",
      citationUrl: "https://www.ecfr.gov/current/title-21/chapter-I/subchapter-G/part-740/section-740.18",
    });
  }
  if (productClass === "hair-dye") {
    warnings.push({
      code: "EU_HAIR_DYE_SENSITISATION_WARNING",
      text: "Hair colorants can cause severe allergic reactions. Read and follow instructions. This product is not intended for use on persons under the age of 16. Temporary 'black henna' tattoos may increase your risk of allergy. Do not colour your hair if you have a rash on your face or sensitive, irritated and damaged scalp; if you have ever experienced any reaction after colouring your hair; or if you have experienced a reaction to a temporary 'black henna' tattoo in the past.",
      citation: "Reg. (EC) 1223/2009 Annex III §11 — hair-dye warning",
      citationUrl: EU_REG_URL,
    });
  }
  if ((mocraFacts.pfasIngredients || []).length > 0) {
    warnings.push({ code: "PFAS_DISCLOSURE", text: `Contains PFAS: ${mocraFacts.pfasIngredients.join(", ")}.`, citation: "FD&C Act § 608", citationUrl: mocraReference.fdaSources?.pfas?.url || mocraReference.metadata.sourceUrl });
  }
  if (isProfessionalUse) {
    warnings.push({ code: "PROFESSIONAL_USE_STATEMENT", text: "For professional use only.", citation: "MoCRA § 364e(b)", citationUrl: mocraReference.metadata.sourceUrl });
  }

  // State-level warnings (FIX #8)
  stateFindings.forEach((finding) => {
    warnings.push({
      code: finding.code,
      text: finding.text,
      citation: finding.citation,
      citationUrl: finding.citationUrl,
      state: finding.state,
      blocking: finding.blocking,
      severity: finding.severity,
    });
  });

  // Net-content dual-unit validation also surfaces as a warning until fixed.
  if (!netContent.hasBoth && product.netContent) {
    warnings.push({
      code: "NET_QUANTITY_DUAL_UNIT_REQUIRED",
      text: `Net content "${product.netContent}" must include both metric and US customary units (e.g., "50 mL / 1.7 fl. oz.").`,
      citation: "FPLA § 4(a)(2)",
      citationUrl: FPLA_URL,
      severity: "error",
      blocking: false,
    });
  }

  const missingFields = requiredFields.filter((f) => !f.present);
  const isReady = missingFields.length === 0;

  // Drug Facts panel — required for OTC sunscreens per FDA's final
  // sunscreen monograph (21 CFR Part 352) and 21 CFR § 201.66 panel format.
  // Only emitted when the product is classified as a sunscreen.
  const spfNumber = extractSpfNumber(product);
  let drugFacts = null;
  if (productClass === "sunscreen") {
    const { actives } = splitUvActives(ifraResults, regulatoryRules);
    drugFacts = {
      heading: "Drug Facts",
      activeIngredients: actives.map((row) => ({
        name: titleCase(row.ingredient_name || ""),
        casNumber: row.cas_number,
        percentage: row.formula_percentage,
        purpose: "Sunscreen",
      })),
      uses: [
        "Helps prevent sunburn.",
        spfNumber && spfNumber >= 15
          ? "If used as directed with other sun protection measures (see Directions), decreases the risk of skin cancer and early skin aging caused by the sun."
          : null,
      ].filter(Boolean),
      warnings: [
        "For external use only.",
        "Do not use on damaged or broken skin.",
        "When using this product keep out of eyes. Rinse with water to remove.",
        "Stop use and ask a doctor if rash occurs.",
        "Keep out of reach of children. If swallowed, get medical help or contact a Poison Control Center right away.",
      ],
      directions: [
        "Apply liberally 15 minutes before sun exposure.",
        "Reapply at least every 2 hours.",
        "Use a water-resistant sunscreen if swimming or sweating.",
        "Children under 6 months of age: ask a doctor.",
        "Sun Protection Measures: Spending time in the sun increases your risk of skin cancer and early skin aging. To decrease this risk, regularly use a sunscreen with a Broad Spectrum SPF value of 15 or higher and other sun protection measures including: limit time in the sun, especially from 10 a.m.–2 p.m.; wear long-sleeved shirts, pants, hats, and sunglasses.",
      ],
      otherInformation: [
        "Protect this product from excessive heat and direct sun.",
      ],
      // Per 21 CFR § 201.66(c)(8), inactive ingredients are listed in
      // alphabetical order under the Drug Facts panel.
      inactiveIngredients: ingredientBuild.fullList
        .filter((entry) => !entry.separator && !entry.isFragranceAggregate)
        .filter((entry) => !actives.some((a) => normalizeText(a.ingredient_name) === normalizeText(entry.ingredient_name || entry.inciName)))
        .map((entry) => entry.inciName)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b)),
      citation: "21 CFR § 201.66 / 21 CFR Part 352 (sunscreen monograph)",
      citationUrl: "https://www.ecfr.gov/current/title-21/chapter-I/subchapter-D/part-352",
    };
  }

  const panels = {
    pdp: {
      heading: "Principal display panel",
      lines: [
        { key: "brand", text: product.brand || "", weight: 600, fontSize: "1rem" },
        { key: "name", text: product.name || activeFormula.name || "", weight: 700, fontSize: "1.5rem" },
        { key: "function", text: product.function || "", color: "#6B7280", fontSize: "0.875rem", required: true },
        ...(productClass === "sunscreen" && spfNumber
          ? [{
              key: "spf-callout",
              text: `Broad Spectrum SPF ${spfNumber}`,
              weight: 700,
              fontSize: "1.1rem",
              color: "#1B2A4A",
              note: "21 CFR § 201.327 — sunscreen PDP claim",
            }]
          : []),
        {
          key: "netContent",
          text: product.netContent || "—",
          fontSize: "0.875rem",
          required: true,
          note: pdpGeometry.minLetterHeightIn
            ? `Min letter height: ${pdpGeometry.minLetterHeightIn.toFixed(3)}″ (${pdpGeometry.minLetterHeightMm} mm) — bottom 30% of PDP`
            : "Bottom 30% of PDP (21 CFR § 701.13)",
        },
      ].filter((line) => line.text || line.required),
    },
    ...(drugFacts
      ? {
          drugFacts: {
            heading: "Drug Facts",
            lines: [
              {
                key: "drug-facts-actives-label",
                text: "Active ingredient (Purpose)",
                weight: 600,
                fontSize: "0.75rem",
                uppercase: true,
              },
              ...drugFacts.activeIngredients.map((a, i) => ({
                key: `drug-facts-active-${i}`,
                text: `${a.name} ${Number.isFinite(Number(a.percentage)) ? `${Number(a.percentage)}% ` : ""}— Sunscreen`,
                fontSize: "0.7rem",
                weight: 500,
              })),
              { key: "drug-facts-uses-label", text: "Uses", weight: 600, fontSize: "0.75rem", uppercase: true, mt: 1 },
              ...drugFacts.uses.map((u, i) => ({
                key: `drug-facts-use-${i}`,
                text: `• ${u}`,
                fontSize: "0.7rem",
                lineHeight: 1.5,
              })),
              { key: "drug-facts-warnings-label", text: "Warnings", weight: 600, fontSize: "0.75rem", uppercase: true, mt: 1 },
              ...drugFacts.warnings.map((w, i) => ({
                key: `drug-facts-warning-${i}`,
                text: `• ${w}`,
                fontSize: "0.7rem",
                lineHeight: 1.5,
              })),
              { key: "drug-facts-directions-label", text: "Directions", weight: 600, fontSize: "0.75rem", uppercase: true, mt: 1 },
              ...drugFacts.directions.map((d, i) => ({
                key: `drug-facts-direction-${i}`,
                text: `• ${d}`,
                fontSize: "0.7rem",
                lineHeight: 1.5,
              })),
              { key: "drug-facts-other-label", text: "Other information", weight: 600, fontSize: "0.75rem", uppercase: true, mt: 1 },
              ...drugFacts.otherInformation.map((o, i) => ({
                key: `drug-facts-other-${i}`,
                text: `• ${o}`,
                fontSize: "0.7rem",
                lineHeight: 1.5,
              })),
              { key: "drug-facts-inactives-label", text: "Inactive ingredients", weight: 600, fontSize: "0.75rem", uppercase: true, mt: 1 },
              {
                key: "drug-facts-inactives",
                text: drugFacts.inactiveIngredients.join(", "),
                fontSize: "0.7rem",
                lineHeight: 1.5,
              },
            ],
          },
        }
      : {}),
    info: {
      heading: "Information panel",
      lines: [
        ...(warnings.length > 0
          ? [
              { key: "warnings-label", text: "Warnings", weight: 600, fontSize: "0.75rem", uppercase: true },
              ...warnings.map((w, i) => ({
                key: `warning-${i}`,
                text: w.text,
                fontSize: "0.7rem",
                color: w.blocking ? "#991B1B" : "#92400E",
                lineHeight: 1.5,
              })),
            ]
          : []),
        { key: "ingredients-label", text: "Ingredients", weight: 600, fontSize: "0.75rem", uppercase: true, mt: 1 },
        { key: "ingredients", text: ingredientLine || "—", fontSize: "0.7rem", lineHeight: 1.5 },
        { key: "rp-label", text: "Distributed by", weight: 600, fontSize: "0.7rem", uppercase: true, mt: 1 },
        { key: "rp-name", text: product.responsiblePerson || "—", fontSize: "0.75rem", required: true },
        { key: "rp-address", text: product.responsibleAddress || "—", fontSize: "0.7rem", color: "#374151", required: true },
        ...(product.countryOfOrigin
          ? [{ key: "country", text: `Made in ${product.countryOfOrigin}`, fontSize: "0.7rem", color: "#374151" }]
          : []),
        {
          key: "adverse-event",
          text: `Report adverse events: ${adverseEventContact || product.responsiblePhone || product.responsibleAddress || "—"}`,
          fontSize: "0.7rem",
          color: "#374151",
          required: true,
          note: "MoCRA § 605",
        },
      ],
    },
  };

  return {
    jurisdiction: "US",
    isReady,
    requiredFields,
    missingFields,
    panels,
    warnings,
    isProfessionalUse,
    productClass,
    spfNumber,
    drugFacts,
    netContent: { ...netContent, required: "Both metric AND US customary (FPLA § 4(a)(2))" },
    pdpGeometry,
    stateFindings,
    pictograms: buildUsPictograms(),
    ingredientList: ingredientBuild.fullList.filter((e) => !e.separator).map((i) => ({
      inciName: i.inciName,
      casNumber: i.cas_number,
      percentage: i.formula_percentage,
      verified: i.concentration_verified === true,
      isColorant: i.isColorant,
      isNano: i.isNano,
    })),
    citations: [
      { article: "FPLA", regulation: "Fair Packaging and Labeling Act", url: FPLA_URL },
      { article: "21 CFR § 701.3 (ingredient declaration)", regulation: "21 CFR § 701", url: CFR_701_URL },
      { article: "21 CFR § 701.11 (statement of identity)", regulation: "21 CFR § 701", url: CFR_701_URL },
      { article: "21 CFR § 701.12 (responsible firm)", regulation: "21 CFR § 701", url: CFR_701_URL },
      { article: "21 CFR § 701.13 (net quantity)", regulation: "21 CFR § 701", url: CFR_701_URL },
      { article: "21 CFR § 740 (warnings)", regulation: "21 CFR § 740", url: CFR_740_URL },
      { article: "MoCRA § 605 (adverse event contact)", regulation: "21 USC § 364c(b)", url: mocraReference.metadata.sourceUrl },
      { article: "19 CFR § 134 (country of origin)", regulation: "19 CFR § 134", url: CFR_134_URL },
      { article: "California Prop 65 (warning)", regulation: "CA H&S § 25249.5", url: PROP_65_URL },
      { article: "CA SB-484 / WA HB 1047 / NY S4265B / MD HB643 (state bans)", regulation: "State cosmetic laws", url: PROP_65_URL },
    ],
  };
};

// ---------------------------------------------------------------------------
// Combined entrypoint with multi-language support
// ---------------------------------------------------------------------------

export const generateLabels = (input = {}) => {
  const { languages, language, ...rest } = input;
  const langs = languages && languages.length ? languages : [language || "en"];
  const eu = langs.map((lng) => generateEuLabel({ ...rest, language: lng }));
  const us = generateUsLabel(rest);
  return {
    EU: eu[0],
    EUByLanguage: Object.fromEntries(eu.map((label) => [label.language, label])),
    US: us,
    summary: {
      euReady: eu[0].isReady,
      usReady: us.isReady,
      euMissing: eu[0].missingFields.length,
      usMissing: us.missingFields.length,
      annexWarnings: eu[0].annexWarnings.length,
      usWarnings: us.warnings.length,
      stateFindings: us.stateFindings.length,
      languagesGenerated: langs,
    },
  };
};

export {
  isColorant,
  isNanoForm,
  computeNetContentMinLetterHeight,
};
